import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Search,
  RefreshCw,
  Eye,
  CheckCircle2,
  Clock,
  Truck,
  AlertTriangle,
  RotateCcw,
  Star,
  Timer,
  MessageSquare,
  X,
  ImageIcon,
  ShoppingCart,
} from "lucide-react";
import OrderDetailDrawer from "./OrderDetailDrawer";
import { useLazyGetAdminOrdersQuery } from "../../../redux/actions/Admin/orderAdmin";

export enum ReviewAction {
  Approve = 1,
  Reject = 2,
  RequestRevision = 3,
}

// Define the context shape received from AdminMainLayout
interface AdminLayoutContext {
  viewMode: "day" | "week" | "month";
  filterMode: "day" | "week" | "month" | "custom";
  headerDates: {
    fromDate?: string;
    toDate?: string;
  };
}

export const getOrderStatusMeta = (status: string) => {
  switch (status) {
    case "PaymentConfirmed":
      return {
        text: "Pending",
        color: "text-amber-700",
        bg: "bg-amber-50",
        border: "border-amber-200",
      };
    case "Payment Pending":
      return {
        text: "Payment Pending",
        color: "text-orange-700",
        bg: "bg-orange-50",
        border: "border-orange-200",
      };
    case "Confirmed":
      return {
        text: "Confirmed",
        color: "text-blue-700",
        bg: "bg-blue-50",
        border: "border-blue-200",
      };
    case "Packed":
      return {
        text: "Packed",
        color: "text-indigo-700",
        bg: "bg-indigo-50",
        border: "border-indigo-200",
      };
    case "Shipped":
      return {
        text: "Shipped",
        color: "text-purple-700",
        bg: "bg-purple-50",
        border: "border-purple-200",
      };
    case "Delivered":
      return {
        text: "Delivered",
        color: "text-emerald-700",
        bg: "bg-emerald-50",
        border: "border-emerald-200",
      };
    case "Cancelled":
      return {
        text: "Cancelled",
        color: "text-red-700",
        bg: "bg-red-50",
        border: "border-red-200",
      };
    case "Refunded":
      return {
        text: "Refunded",
        color: "text-slate-700",
        bg: "bg-slate-50",
        border: "border-slate-200",
      };
    default:
      return {
        text: status || "Processing",
        color: "text-gray-700",
        bg: "bg-gray-50",
        border: "border-gray-200",
      };
  }
};

export const CustomerTierBadge = ({ tier }: { tier: string }) => {
  const normalizedTier = tier?.toLowerCase();

  const styles =
    normalizedTier === "new"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : normalizedTier === "bronz" || normalizedTier === "bronze"
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : normalizedTier === "silver"
          ? "bg-slate-100 text-slate-700 border-slate-200"
          : normalizedTier === "gold"
            ? "bg-yellow-50 text-yellow-700 border-yellow-200"
            : normalizedTier === "platinum" || normalizedTier === "premium"
              ? "bg-violet-50 text-violet-700 border-violet-200"
              : "bg-gray-50 text-gray-600 border-gray-200";

  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wide ${styles}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5 shrink-0" />
      {tier}
    </span>
  );
};

export const formatToUtcIsoString = (dateVal?: string | Date | null): string => {
  if (!dateVal) return "";
  const date = typeof dateVal === "string" ? new Date(dateVal) : dateVal;
  return isNaN(date.getTime()) ? "" : date.toISOString();
};

const AdminOrdersDashboard: React.FC = () => {
  const { filterMode, headerDates } = useOutletContext<AdminLayoutContext>();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [abandonedCartFilter, setAbandonedCartFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState("all");
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  const [pageInput, setPageInput] = useState("1");
  const [activeReviewOrderId, setActiveReviewOrderId] = useState<number | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const [getOrders, { data: ordersResponse }] = useLazyGetAdminOrdersQuery();

  const orders = ordersResponse?.data || [];
  const totalRecords = (ordersResponse as any)?.totalRecords || 0;

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const getAbandonedCartParam = (val: string) => {
    if (val === "true") return true;
    if (val === "false") return false;
    return undefined;
  };

  const loadOrders = (
    currentPage: number,
    currentSearch: string,
    currentStatus: string,
    currentAbandonedFilter: string
  ) => {
    const utcStartDate = headerDates?.fromDate
      ? new Date(headerDates.fromDate).toISOString()
      : "";

    const utcEndDate = headerDates?.toDate
      ? new Date(headerDates.toDate).toISOString()
      : "";

    getOrders({
      page: currentPage,
      pageSize,
      search: currentSearch.trim(),
      orderStatus: currentStatus === "all" ? "" : currentStatus,
      isFromAbandonedCart: getAbandonedCartParam(currentAbandonedFilter),
      period: filterMode !== "custom" ? filterMode : "",
      startDate: utcStartDate,
      endDate: utcEndDate,
    });
  };

  const handleRefreshLogs = async () => {
    setIsRefreshing(true);

    const utcStartDate = headerDates?.fromDate
      ? new Date(headerDates.fromDate).toISOString()
      : "";

    const utcEndDate = headerDates?.toDate
      ? new Date(headerDates.toDate).toISOString()
      : "";

    await getOrders({
      page,
      pageSize,
      search: searchQuery.trim(),
      orderStatus: statusFilter === "all" ? "" : statusFilter,
      isFromAbandonedCart: getAbandonedCartParam(abandonedCartFilter),
      period: filterMode !== "custom" ? filterMode : "",
      startDate: utcStartDate,
      endDate: utcEndDate,
    }).unwrap();

    setIsRefreshing(false);
  };

  // Trigger reload when pagination, status filter, cart filter, or header dates/mode update
  useEffect(() => {
    loadOrders(page, searchQuery, statusFilter, abandonedCartFilter);
  }, [page, statusFilter, abandonedCartFilter, filterMode, headerDates?.fromDate, headerDates?.toDate]);

  // Debounced search trigger (resets to page 1)
  useEffect(() => {
    const handler = setTimeout(() => {
      loadOrders(1, searchQuery, statusFilter, abandonedCartFilter);
      setPage(1);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const formatRemainingTime = (minutes: number): string => {
    if (!minutes || minutes <= 0) return "";

    const days = Math.floor(minutes / (24 * 60));
    const hours = Math.floor((minutes % (24 * 60)) / 60);
    const mins = minutes % 60;

    if (days > 0) {
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  const stats = [
    {
      label: "Pending",
      value: orders.filter((x: any) => x.orderStatus === "Pending" || x.orderStatus === "PaymentConfirmed").length,
      icon: Clock,
    },
    {
      label: "Confirmed",
      value: orders.filter((x: any) => x.orderStatus === "Confirmed").length,
      icon: CheckCircle2,
    },
    {
      label: "Cancelled",
      value: orders.filter((x: any) => x.orderStatus === "Cancelled").length,
      icon: AlertTriangle,
    },
    {
      label: "Paid",
      value: orders.filter((x: any) => x.paymentStatus === "Paid").length,
      icon: Truck,
    },
  ];

  const advancedFilterOptions = [
    { value: "all", label: "All Filters" },
    { value: "customerTier", label: "Customer Tier" },
    { value: "customerName", label: "Customer Name" },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 4) {
        pages.push("...");
      }

      const startPage = Math.max(2, page - 1);
      const endPage = Math.min(totalPages - 1, page + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (page < totalPages - 3) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="min-h-screen relative">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-extrabold tracking-tight"
              style={{ color: "#2F5E2E" }}
            >
              Order Management
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              View, track, and manage all customer orders
            </p>
          </div>
          <button
            onClick={handleRefreshLogs}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-sm bg-[#6DBE45] cursor-pointer hover:bg-[#5EAB3C] transition-colors"
          >
            <RefreshCw
              size={16}
              className={isRefreshing ? "animate-spin" : ""}
            />
            {isRefreshing ? "Syncing..." : "Sync Orders"}
          </button>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {stat.label}
                    </p>
                    <p className="text-3xl text-slate-900 font-black">
                      {stat.value}
                    </p>
                  </div>
                  <Icon size={20} style={{ color: "#8B6B3E" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Toolbar Filters Panel */}
        <div className="flex flex-col lg:flex-row gap-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search by Order ID, number, or customer name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:bg-white"
            />
          </div>

          <div className="relative flex-1 sm:flex-initial min-w-[180px]">
            <select
              value={abandonedCartFilter}
              onChange={(e) => {
                setAbandonedCartFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium appearance-none focus:outline-none"
            >
              <option value="all">Cart: All Sources</option>
              <option value="true">Abandoned Cart Recovered</option>
              <option value="false">Direct Orders</option>
            </select>
          </div>

          <div className="relative flex-1 sm:flex-initial min-w-[180px]">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium appearance-none focus:outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="PaymentConfirmed">Pending</option>
              <option value="Payment Pending">Payment Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Packed">Packed</option>
              <option value="Shipped">Shipped</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>

          <div className="relative flex-1 sm:flex-initial min-w-[180px]">
            <select
              value={advancedFilter}
              onChange={(e) => setAdvancedFilter(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium appearance-none focus:outline-none"
            >
              {advancedFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Orders Data Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-visible">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70">
                  <th className="px-6 py-4 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Order Number
                  </th>
                  <th className="px-6 py-4 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Reviews & Rating
                  </th>
                  <th className="px-6 py-4 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Payment Method
                  </th>
                  <th className="px-6 py-4 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Order / Revision Status
                  </th>
                  <th className="px-6 py-4 text-right font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Total Amount
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-slate-500 text-xs uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.length > 0 ? (
                  orders.map((order: any) => {
                    const currentMeta = getOrderStatusMeta(order.orderStatus);
                    const itemReviewsList = order.itemReviews || [];
                    const itemReviewsCount = itemReviewsList.length;
                    const isTooltipOpen = activeReviewOrderId === order.orderId;

                    return (
                      <tr
                        key={order.orderId}
                        className="hover:bg-slate-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4">
                          <p className="font-bold text-slate-900">
                            {order.orderNumber || "-"}
                          </p>

                          {/* Tags Section */}
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {order.isFromAbandonedCart && (
                              <span
                                title="Order recovered from abandoned cart"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-amber-50 text-amber-800 border border-amber-200"
                              >
                                <ShoppingCart size={15} />
                              </span>
                            )}
                            {order.hasReplacementRequest && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-indigo-50 text-indigo-800 border border-indigo-200">
                                <RotateCcw size={9} /> Replacement
                              </span>
                            )}

                          </div>
                        </td>

                        <td className="px-6 py-4 text-slate-600 font-medium whitespace-nowrap">
                          {new Date(order.createdOn).toLocaleDateString()}
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex flex-col items-start gap-1">
                            <p className="font-bold text-slate-900 text-sm leading-5 min-w-0">
                              {order.customerName}
                            </p>

                            <CustomerTierBadge
                              tier={order.customerTag || "New"}
                            />
                          </div>
                        </td>

                        <td className="px-6 py-4 relative">
                          <div className="space-y-1.5">
                            {order.rating ? (
                              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                                <div className="flex items-center">
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                      key={s}
                                      size={11}
                                      className={
                                        s <= order.rating
                                          ? "fill-amber-400 text-amber-400"
                                          : "text-slate-200"
                                      }
                                    />
                                  ))}
                                </div>
                                <span className="text-amber-700 text-[11px]">
                                  {order.rating}/5
                                </span>
                              </div>
                            ) : null}

                            {itemReviewsCount > 0 ? (
                              <div className="relative inline-block">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveReviewOrderId(
                                      isTooltipOpen ? null : order.orderId
                                    );
                                  }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition-colors shadow-2xs cursor-pointer"
                                >
                                  <MessageSquare size={12} />
                                  <span>
                                    {itemReviewsCount} Product Review
                                    {itemReviewsCount > 1 ? "s" : ""}
                                  </span>
                                </button>

                                {isTooltipOpen && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute left-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-3.5 z-50 space-y-3 text-left"
                                  >
                                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                      <div className="flex items-center gap-1.5 text-xs font-bold text-[#2F5E2E]">
                                        <MessageSquare size={13} />
                                        <span>
                                          Product Reviews ({itemReviewsCount})
                                        </span>
                                      </div>
                                      <button
                                        onClick={() => setActiveReviewOrderId(null)}
                                        className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                                      >
                                        <X size={13} />
                                      </button>
                                    </div>

                                    <div className="space-y-3 divide-y divide-slate-100">
                                      {itemReviewsList.map((rev: any, idx: number) => {
                                        const productName =
                                          rev.productName ||
                                          (rev.productId
                                            ? `Product #${rev.productId}`
                                            : `Item #${rev.orderItemId}`);
                                        const images = rev.reviewImages || [];

                                        return (
                                          <div
                                            key={rev.reviewId || idx}
                                            className="pt-2.5 first:pt-0 space-y-1.5"
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex items-center gap-2 min-w-0">
                                                {rev.productImage ? (
                                                  <img
                                                    src={rev.productImage}
                                                    alt={productName}
                                                    className="w-8 h-8 rounded-lg object-cover border border-slate-200 shrink-0"
                                                  />
                                                ) : (
                                                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                                                    <ImageIcon size={12} />
                                                  </div>
                                                )}
                                                <span
                                                  className="font-bold text-xs text-slate-800 truncate"
                                                  title={productName}
                                                >
                                                  {productName}
                                                </span>
                                              </div>

                                              <div className="flex items-center gap-0.5 shrink-0 pt-0.5">
                                                {[1, 2, 3, 4, 5].map((s) => (
                                                  <Star
                                                    key={s}
                                                    size={10}
                                                    className={
                                                      s <= rev.rating
                                                        ? "fill-amber-400 text-amber-400"
                                                        : "text-slate-200"
                                                    }
                                                  />
                                                ))}
                                              </div>
                                            </div>

                                            {rev.comment ? (
                                              <p className="text-xs text-slate-600 italic bg-slate-50 p-2 rounded-xl border border-slate-100 leading-relaxed">
                                                "{rev.comment}"
                                              </p>
                                            ) : (
                                              <p className="text-[11px] text-slate-400 italic">
                                                No written comment provided.
                                              </p>
                                            )}

                                            {images.length > 0 && (
                                              <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                                                {images.map(
                                                  (imgUrl: string, imgIdx: number) => (
                                                    <img
                                                      key={imgIdx}
                                                      src={imgUrl}
                                                      alt="Review photo"
                                                      onClick={() => setPreviewImageUrl(imgUrl)}
                                                      className="w-10 h-10 rounded-lg object-cover border border-slate-200 cursor-pointer hover:opacity-80 transition-opacity"
                                                    />
                                                  )
                                                )}
                                              </div>
                                            )}

                                            {rev.createdOn && (
                                              <p className="text-[9px] text-slate-400 text-right">
                                                {new Date(rev.createdOn).toLocaleString(
                                                  undefined,
                                                  {
                                                    dateStyle: "short",
                                                    timeStyle: "short",
                                                  }
                                                )}
                                              </p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : !order.rating ? (
                              <span className="text-slate-400 text-xs">
                                No Reviews
                              </span>
                            ) : null}
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 rounded text-xs font-bold bg-slate-100 text-slate-700">
                            {order.paymentMethod}
                          </span>
                        </td>

                        <td className="px-6 py-4">
                          {order.hasRevision ? (
                            <div className="space-y-1">
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-300">
                                <Timer size={11} /> Revision:{" "}
                                {order.revisionStatus || "Pending"}
                              </span>
                              {order.remainingMinutes > 0 && (
                                <p className="text-[10px] font-semibold text-amber-700 pl-1">
                                  {formatRemainingTime(order.remainingMinutes)}{" "}
                                  remaining
                                </p>
                              )}
                            </div>
                          ) : (
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold border ${currentMeta.bg} ${currentMeta.color} ${currentMeta.border}`}
                            >
                              {currentMeta.text}
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-right font-black text-slate-900">
                          AED {(order.totalAmount || 0).toFixed(2)}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => setSelectedOrderId(order.orderId)}
                            className="inline-flex items-center justify-center p-2 rounded-xl bg-slate-100 hover:bg-[#5FB63F] hover:text-white transition-all shadow-xs cursor-pointer"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-12 text-center text-slate-400 font-medium"
                    >
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                Page {page} of {totalPages} ({totalRecords} items)
              </span>

              {/* Pagination */}
              <div className="flex items-center gap-1.5">
                {/* First */}
                <button
                  type="button"
                  disabled={!hasPreviousPage}
                  onClick={() => setPage(1)}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  First
                </button>

                {/* Previous */}
                <button
                  type="button"
                  disabled={!hasPreviousPage}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((pageNumber, index) =>
                  pageNumber === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-slate-400 text-xs font-bold"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => setPage(pageNumber as number)}
                      className={`min-w-[34px] px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors cursor-pointer ${page === pageNumber
                          ? "bg-[#6DBE45] text-white border-[#6DBE45]"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                    >
                      {pageNumber}
                    </button>
                  )
                )}

                {/* Next */}
                <button
                  type="button"
                  disabled={!hasNextPage}
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Next
                </button>

                {/* Last */}
                <button
                  type="button"
                  disabled={!hasNextPage}
                  onClick={() => setPage(totalPages)}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Last
                </button>
              </div>

              {/* Go To Page */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">
                  Go to page
                </span>

                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const enteredPage = Number(pageInput);
                      if (
                        Number.isInteger(enteredPage) &&
                        enteredPage >= 1 &&
                        enteredPage <= totalPages
                      ) {
                        setPage(enteredPage);
                      } else {
                        setPageInput(String(page));
                      }
                    }
                  }}
                  className="w-16 px-2.5 py-1.5 text-center text-xs font-bold border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#6DBE45]/30 focus:border-[#6DBE45]"
                />

                <button
                  type="button"
                  onClick={() => {
                    const enteredPage = Number(pageInput);
                    if (
                      Number.isInteger(enteredPage) &&
                      enteredPage >= 1 &&
                      enteredPage <= totalPages
                    ) {
                      setPage(enteredPage);
                    } else {
                      setPageInput(String(page));
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg bg-[#6DBE45] text-white text-xs font-bold hover:bg-[#5EAB3C] transition-colors cursor-pointer"
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-Size Image Preview Modal */}
      {previewImageUrl && (
        <div
          onClick={() => setPreviewImageUrl(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 cursor-pointer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-lg max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-2"
          >
            <button
              onClick={() => setPreviewImageUrl(null)}
              className="absolute top-3 right-3 p-1.5 bg-black/60 hover:bg-black text-white rounded-full transition-colors z-10 cursor-pointer"
            >
              <X size={16} />
            </button>
            <img
              src={previewImageUrl}
              alt="Full size review attachment"
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}

      {/* Order Details Drawer Overlay */}
      {selectedOrderId !== null && (
        <OrderDetailDrawer
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onRefreshDashboard={() =>
            loadOrders(page, searchQuery, statusFilter, abandonedCartFilter)
          }
        />
      )}
    </div>
  );
};

export default AdminOrdersDashboard;